import { useEffect, useState } from 'react';
import { Wrench } from 'lucide-react';
import type { PublicMaintenance } from '../types.ts';

interface Props {
  maintenances: PublicMaintenance[];
}

const fmtDateTime = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

function useMinuteTick() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);
}

function formatCountdown(targetDate: string): string {
  const diff = new Date(targetDate).getTime() - Date.now();
  if (diff <= 0) return 'Em andamento';
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  parts.push(`${minutes}min`);
  return `em ${parts.join(' ')}`;
}

export function ScheduledMaint({ maintenances }: Props) {
  useMinuteTick();

  if (maintenances.length === 0) return null;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
      <h2 className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">
        Manutenções Programadas
      </h2>

      <div className="space-y-3">
        {maintenances.map((m, idx) => {
          const isOngoing = new Date(m.iniciadoEm).getTime() <= Date.now();
          return (
            <div
              key={idx}
              className="flex items-start gap-3 rounded-lg border p-4"
              style={{
                borderColor: 'var(--color-status-maintenance)',
                backgroundColor: isOngoing ? 'rgba(37, 99, 235, 0.05)' : undefined,
              }}
            >
              <Wrench
                size={16}
                className="mt-0.5 shrink-0"
                style={{ color: 'var(--color-status-maintenance)' }}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-gray-900 dark:text-white">{m.titulo}</p>
                  <span
                    className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white"
                    style={{ backgroundColor: 'var(--color-status-maintenance)' }}
                  >
                    {isOngoing ? 'Em andamento' : formatCountdown(m.iniciadoEm)}
                  </span>
                </div>
                {m.descricao && (
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{m.descricao}</p>
                )}
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  {fmtDateTime.format(new Date(m.iniciadoEm))} — {fmtDateTime.format(new Date(m.encerradoEm))}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
